import React, { useState, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Calendar, Download, RefreshCw, Filter, DollarSign, TrendingUp,
  Package, AlertCircle, Globe, Users, Building2, FileText, Loader2,
  ChevronDown, ChevronRight, X, Search, Receipt, Shield, BarChart3
} from 'lucide-react';
import * as XLSX from 'xlsx';

const fmt = (v) => v == null || isNaN(v) ? '$0' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (v) => v == null || isNaN(v) ? '0.0%' : (Number(v) * 100).toFixed(1) + '%';
const fmtNum = (v) => v == null || isNaN(v) ? '0' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 });

export default function TariffTrackerDashboard({ embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [unmatched, setUnmatched] = useState({ count: 0, duty: 0, tariffs: 0, customs_value: 0 });
  const [error, setError] = useState(null);

  // Date range — default last 6 months
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const [startDate, setStartDate] = useState(sixMonthsAgo.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(now.toISOString().slice(0, 10));

  // Post-load multi-select filters
  const [filters, setFilters] = useState({ customer: [], division: [], country: [] });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchTerms, setSearchTerms] = useState({ customer: '', division: '', country: '' });

  // Detail table expand
  const [showDetail, setShowDetail] = useState(true);

  // ---- Tariff by Regime state ----
  const [regimeData, setRegimeData] = useState([]);
  const [regimeLoading, setRegimeLoading] = useState(false);
  const [regimeError, setRegimeError] = useState(null);
  const [showRegime, setShowRegime] = useState(true);
  const [regimeDivFilter, setRegimeDivFilter] = useState([]);
  const [regimeImpFilter, setRegimeImpFilter] = useState([]);
  const [regimeCountryFilter, setRegimeCountryFilter] = useState([]);

  // ---- Load Data ----
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRegimeLoading(true);
    setRegimeError(null);
    const functions = getFunctions();

    // Fire both queries in parallel
    const trackerPromise = httpsCallable(functions, 'getTariffTrackerData', { timeout: 150000 })({ startDate, endDate })
      .then(result => {
        setData(result.data.data || []);
        setUnmatched(result.data.unmatched || { count: 0, duty: 0, tariffs: 0, customs_value: 0 });
        setFilters({ customer: [], division: [], country: [] });
      })
      .catch(err => {
        console.error('Tariff tracker load error:', err);
        setError(err.message || 'Failed to load tariff data');
      })
      .finally(() => setLoading(false));

    const regimePromise = httpsCallable(functions, 'getTariffByRegimeData', { timeout: 150000 })({ startDate, endDate })
      .then(result => {
        setRegimeData(result.data.data || []);
        setRegimeDivFilter([]);
        setRegimeImpFilter([]);
        setRegimeCountryFilter([]);
      })
      .catch(err => {
        console.error('Tariff by regime load error:', err);
        setRegimeError(err.message || 'Failed to load regime data');
      })
      .finally(() => setRegimeLoading(false));

    await Promise.all([trackerPromise, regimePromise]);
  }, [startDate, endDate]);

  // ---- Dropdown options ----
  const dropdownOptions = useMemo(() => {
    if (!data.length) return { customers: [], divisions: [], countries: [] };
    return {
      customers: [...new Set(data.map(r => r.customer).filter(Boolean))].sort(),
      divisions: [...new Set(data.map(r => r.division).filter(Boolean))].sort(),
      countries: [...new Set(data.map(r => r.country).filter(Boolean))].sort(),
    };
  }, [data]);

  // ---- Filtered data ----
  const filteredData = useMemo(() => {
    let result = data;
    if (filters.customer.length) result = result.filter(r => filters.customer.includes(r.customer));
    if (filters.division.length) result = result.filter(r => filters.division.includes(r.division));
    if (filters.country.length) result = result.filter(r => filters.country.includes(r.country));
    return result;
  }, [data, filters]);

  // ---- Aggregation helpers ----
  const aggregate = (rows) => {
    const customs_value = rows.reduce((s, r) => s + (Number(r.customs_value) || 0), 0);
    const total_duty = rows.reduce((s, r) => s + (Number(r.total_duty) || 0), 0);
    const tariffs_allocated = rows.reduce((s, r) => s + (Number(r.tariffs_allocated) || 0), 0);
    const actual_tariff = rows.reduce((s, r) => s + (Number(r.actual_tariff) || 0), 0);
    const system_tariff = rows.reduce((s, r) => s + (Number(r.system_tariff) || 0), 0);
    const tariff_variance = rows.reduce((s, r) => s + (Number(r.tariff_variance) || 0), 0);
    const duty_variance = rows.reduce((s, r) => s + (Number(r.duty_variance) || 0), 0);
    const qty = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
    const total_fob = rows.reduce((s, r) => s + (Number(r.total_fob) || 0), 0);
    const tariff_rate = customs_value > 0 ? actual_tariff / customs_value : 0;
    return { customs_value, total_duty, tariffs_allocated, actual_tariff, system_tariff, tariff_variance, duty_variance, qty, total_fob, tariff_rate, count: rows.length };
  };

  // ---- Grand totals ----
  const totals = useMemo(() => aggregate(filteredData), [filteredData]);

  // ---- Group by dimension ----
  const groupBy = (rows, key) => {
    const map = {};
    rows.forEach(r => {
      const k = r[key] || 'Unknown';
      if (!map[k]) map[k] = [];
      map[k].push(r);
    });
    return Object.entries(map)
      .map(([name, group]) => ({ name, ...aggregate(group) }))
      .sort((a, b) => b.actual_tariff - a.actual_tariff);
  };

  const byCustomer = useMemo(() => groupBy(filteredData, 'customer'), [filteredData]);
  const byDivision = useMemo(() => groupBy(filteredData, 'division'), [filteredData]);
  const byCountry = useMemo(() => groupBy(filteredData, 'country'), [filteredData]);

  // ---- Filter helpers ----
  const toggleFilter = (dimension, value) => {
    setFilters(prev => {
      const current = prev[dimension];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [dimension]: next };
    });
  };

  const setFilterFromRow = (dimension, value) => {
    setFilters(prev => {
      const current = prev[dimension];
      // Toggle: if it's the only one selected, clear it
      if (current.length === 1 && current[0] === value) {
        return { ...prev, [dimension]: [] };
      }
      return { ...prev, [dimension]: [value] };
    });
  };

  const clearAllFilters = () => setFilters({ customer: [], division: [], country: [] });
  const activeFilterCount = filters.customer.length + filters.division.length + filters.country.length;

  // ---- Excel export ----
  const exportToExcel = useCallback(() => {
    if (!filteredData.length) return;
    const ws = XLSX.utils.json_to_sheet(filteredData.map(r => ({
      Customer: r.customer,
      Division: r.division,
      Country: r.country,
      Month: r.month,
      Office: r.office,
      Invoice: r.commercial_invoice,
      Style: r.style,
      Qty: r.qty,
      'Customs Value': r.customs_value,
      'Total Duty': r.total_duty,
      'Tariffs Allocated': r.tariffs_allocated,
      'Actual Tariff': r.actual_tariff,
      'System Tariff': r.system_tariff,
      'Actual Tariff Rate': r.actual_tariff_rate,
      'System Tariff Rate': r.system_tariff_rate,
      'Tariff Variance': r.tariff_variance,
      'Duty Variance': r.duty_variance,
      'Total FOB': r.total_fob,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tariff Tracker');
    XLSX.writeFile(wb, `tariff_tracker_${startDate}_${endDate}.xlsx`);
  }, [filteredData, startDate, endDate]);

  // ---- Regime data processing ----
  const impFilterFn = (r) => {
    const imp = r.importer_name || r.importer_number || 'Unknown';
    return regimeImpFilter.includes(imp);
  };

  // Apply country filter helper
  const applyCountryFilter = (data) => regimeCountryFilter.length ? data.filter(r => regimeCountryFilter.includes(r.country)) : data;

  // Full filter (importer + division + country) for summary cards, monthly pivot, export
  const filteredRegimeData = useMemo(() => {
    let result = regimeData;
    if (regimeDivFilter.length) result = result.filter(r => regimeDivFilter.includes(r.division));
    if (regimeImpFilter.length) result = result.filter(impFilterFn);
    if (regimeCountryFilter.length) result = result.filter(r => regimeCountryFilter.includes(r.country));
    return result;
  }, [regimeData, regimeDivFilter, regimeImpFilter, regimeCountryFilter]);

  // Filtered by division + country only (for importer table — so it doesn't filter itself)
  const regimeDataForImporter = useMemo(() => {
    let result = regimeData;
    if (regimeDivFilter.length) result = result.filter(r => regimeDivFilter.includes(r.division));
    if (regimeCountryFilter.length) result = result.filter(r => regimeCountryFilter.includes(r.country));
    return result;
  }, [regimeData, regimeDivFilter, regimeCountryFilter]);

  // Filtered by importer + country only (for division table — so it doesn't filter itself)
  const regimeDataForDivision = useMemo(() => {
    let result = regimeData;
    if (regimeImpFilter.length) result = result.filter(impFilterFn);
    if (regimeCountryFilter.length) result = result.filter(r => regimeCountryFilter.includes(r.country));
    return result;
  }, [regimeData, regimeImpFilter, regimeCountryFilter]);

  const regimeMonths = useMemo(() =>
    [...new Set(filteredRegimeData.map(r => r.entry_month))].sort(),
  [filteredRegimeData]);

  const regimeDivisions = useMemo(() =>
    [...new Set(regimeData.map(r => r.division).filter(Boolean))].sort(),
  [regimeData]);

  const regimeCountries = useMemo(() =>
    [...new Set(regimeData.map(r => r.country).filter(Boolean))].sort(),
  [regimeData]);

  const regimeImporters = useMemo(() =>
    [...new Set(regimeData.map(r => r.importer_name || r.importer_number || 'Unknown').filter(Boolean))].sort(),
  [regimeData]);

  const TARIFF_TYPES = ['Base Tariff', 'Fentanyl', 'Reciprocal', '2018 Tariff'];
  const REGIME_COLORS = { 'Base Tariff': 'slate', 'Fentanyl': 'red', 'Reciprocal': 'violet', '2018 Tariff': 'amber' };
  const REGIME_BG = { 'Base Tariff': 'bg-slate-100', 'Fentanyl': 'bg-red-50', 'Reciprocal': 'bg-violet-50', '2018 Tariff': 'bg-amber-50' };
  const REGIME_TEXT = { 'Base Tariff': 'text-slate-700', 'Fentanyl': 'text-red-700', 'Reciprocal': 'text-violet-700', '2018 Tariff': 'text-amber-700' };

  // Pivot: tariff_type × month
  const regimePivot = useMemo(() => {
    const map = {};
    TARIFF_TYPES.forEach(t => { map[t] = { type: t, total: 0 }; });
    filteredRegimeData.forEach(r => {
      const t = r.tariff_type || 'Base Tariff';
      if (!map[t]) map[t] = { type: t, total: 0 };
      const duty = Number(r.total_duty) || 0;
      map[t][r.entry_month] = (map[t][r.entry_month] || 0) + duty;
      map[t].total += duty;
    });
    return TARIFF_TYPES.map(t => map[t]).filter(r => r.total > 0);
  }, [filteredRegimeData, regimeMonths]);

  // By importer × tariff type (filtered by division only, not by importer)
  const regimeByImporter = useMemo(() => {
    const map = {};
    regimeDataForImporter.forEach(r => {
      const imp = r.importer_name || r.importer_number || 'Unknown';
      if (!map[imp]) map[imp] = { importer: imp, total: 0 };
      const duty = Number(r.total_duty) || 0;
      const t = r.tariff_type || 'Base Tariff';
      map[imp][t] = (map[imp][t] || 0) + duty;
      map[imp].total += duty;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [regimeDataForImporter]);

  // By division × tariff type (filtered by importer only, not by division)
  const regimeByDivision = useMemo(() => {
    const map = {};
    regimeDataForDivision.forEach(r => {
      const div = r.division || 'Unknown';
      if (!map[div]) map[div] = { division: div, total: 0 };
      const duty = Number(r.total_duty) || 0;
      const t = r.tariff_type || 'Base Tariff';
      map[div][t] = (map[div][t] || 0) + duty;
      map[div].total += duty;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [regimeDataForDivision]);

  // Regime summary totals
  const regimeTotals = useMemo(() => {
    const totals = { goods: 0, duty: 0 };
    TARIFF_TYPES.forEach(t => { totals[t] = 0; });
    filteredRegimeData.forEach(r => {
      totals.goods += Number(r.total_goods_value) || 0;
      totals.duty += Number(r.total_duty) || 0;
      const t = r.tariff_type || 'Base Tariff';
      totals[t] = (totals[t] || 0) + (Number(r.total_duty) || 0);
    });
    return totals;
  }, [filteredRegimeData]);

  // Regime Excel export
  const exportRegimeExcel = useCallback(() => {
    if (!filteredRegimeData.length) return;
    const ws = XLSX.utils.json_to_sheet(filteredRegimeData.map(r => ({
      Month: r.entry_month,
      Division: r.division,
      Country: r.country,
      'Tariff Type': r.tariff_type,
      'Tariff Regime': r.tariff_regime,
      HTS: r.hts_number_full,
      Entries: r.entries,
      'Goods Value': r.total_goods_value,
      'Duty Amount': r.total_duty,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tariff By Regime');
    XLSX.writeFile(wb, `tariff_by_regime_${startDate}_${endDate}.xlsx`);
  }, [filteredRegimeData, startDate, endDate]);

  // ---- Multi-select dropdown component ----
  const MultiSelectDropdown = ({ dimension, label, options, icon: Icon }) => {
    const isOpen = openDropdown === dimension;
    const selected = filters[dimension];
    const search = searchTerms[dimension] || '';
    const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

    return (
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(isOpen ? null : dimension)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
            selected.length > 0
              ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          <Icon className="h-4 w-4" />
          {label}
          {selected.length > 0 && (
            <span className="bg-indigo-600 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3 w-3" />
        </button>
        {isOpen && (
          <div className="absolute z-50 top-full mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                <input
                  type="text"
                  placeholder={`Search ${label.toLowerCase()}...`}
                  value={search}
                  onChange={(e) => setSearchTerms(prev => ({ ...prev, [dimension]: e.target.value }))}
                  className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-indigo-400"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {filtered.map(opt => (
                <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleFilter(dimension, opt)}
                    className="rounded border-slate-300 text-indigo-600"
                  />
                  <span className="truncate">{opt}</span>
                </label>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
              )}
            </div>
            {selected.length > 0 && (
              <div className="border-t border-slate-100 p-2">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, [dimension]: [] }))}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Clear {label}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---- Summary Table Component ----
  const SummaryTable = ({ title, icon: Icon, rows, dimension, color }) => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className={`px-4 py-3 border-b border-slate-100 bg-${color}-50/50`}>
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Icon className={`h-4 w-4 text-${color}-600`} />
          {title}
          <span className="text-xs text-slate-400 font-normal">({rows.length})</span>
        </h3>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-slate-600">{title.replace('By ', '')}</th>
              <th className="text-right px-3 py-2 font-medium text-slate-600">Customs Value</th>
              <th className="text-right px-3 py-2 font-medium text-slate-600">Duty</th>
              <th className="text-right px-3 py-2 font-medium text-slate-600">Tariffs</th>
              <th className="text-right px-3 py-2 font-medium text-slate-600">Rate</th>
              <th className="text-right px-3 py-2 font-medium text-slate-600">Variance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isSelected = filters[dimension]?.includes(row.name);
              return (
                <tr
                  key={row.name}
                  onClick={() => setFilterFromRow(dimension, row.name)}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 border-l-2 border-indigo-500'
                      : i % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100'
                  }`}
                >
                  <td className="px-3 py-2 font-medium text-slate-800 truncate max-w-[180px]">{row.name}</td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmt(row.customs_value)}</td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmt(row.total_duty)}</td>
                  <td className="text-right px-3 py-2 text-slate-700 font-medium">{fmt(row.actual_tariff)}</td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmtPct(row.tariff_rate)}</td>
                  <td className={`text-right px-3 py-2 font-medium ${row.tariff_variance > 0 ? 'text-red-600' : row.tariff_variance < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                    {fmt(row.tariff_variance)}
                  </td>
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                <td className="px-3 py-2 text-slate-800">Total</td>
                <td className="text-right px-3 py-2">{fmt(rows.reduce((s, r) => s + r.customs_value, 0))}</td>
                <td className="text-right px-3 py-2">{fmt(rows.reduce((s, r) => s + r.total_duty, 0))}</td>
                <td className="text-right px-3 py-2">{fmt(rows.reduce((s, r) => s + r.actual_tariff, 0))}</td>
                <td className="text-right px-3 py-2">
                  {fmtPct(rows.reduce((s, r) => s + r.customs_value, 0) > 0
                    ? rows.reduce((s, r) => s + r.actual_tariff, 0) / rows.reduce((s, r) => s + r.customs_value, 0)
                    : 0)}
                </td>
                <td className="text-right px-3 py-2">{fmt(rows.reduce((s, r) => s + r.tariff_variance, 0))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header + Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-slate-800">Tariff Tracker</h2>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            />
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {loading ? 'Loading...' : 'Load'}
            </button>
          </div>
        </div>

      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* No data prompt */}
      {!loading && regimeData.length === 0 && !error && !regimeError && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Select a date range and click Load to view tariff data.</p>
        </div>
      )}

      {/* ========== TARIFF BY REGIME SECTION ========== */}
      {regimeData.length > 0 && (
        <>
          {/* Regime Header */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mt-2">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-violet-600" />
                <h2 className="text-lg font-semibold text-slate-800">Tariff by Regime</h2>
                <span className="text-xs text-slate-400">(Entry Summary Data)</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {regimeImporters.length > 1 && (
                  <select
                    value={regimeImpFilter.length === 1 ? regimeImpFilter[0] : ''}
                    onChange={(e) => setRegimeImpFilter(e.target.value ? [e.target.value] : [])}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
                  >
                    <option value="">All Importers</option>
                    {regimeImporters.map(imp => (
                      <option key={imp} value={imp}>{imp}</option>
                    ))}
                  </select>
                )}
                {regimeDivisions.length > 0 && (
                  <select
                    value={regimeDivFilter.length === 1 ? regimeDivFilter[0] : ''}
                    onChange={(e) => setRegimeDivFilter(e.target.value ? [e.target.value] : [])}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-violet-400"
                  >
                    <option value="">All Divisions</option>
                    {regimeDivisions.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                )}
                {regimeCountries.length > 0 && (
                  <select
                    value={regimeCountryFilter.length === 1 ? regimeCountryFilter[0] : ''}
                    onChange={(e) => setRegimeCountryFilter(e.target.value ? [e.target.value] : [])}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-amber-400"
                  >
                    <option value="">All Countries</option>
                    {regimeCountries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )}
                {(regimeImpFilter.length > 0 || regimeDivFilter.length > 0 || regimeCountryFilter.length > 0) && (
                  <button
                    onClick={() => { setRegimeImpFilter([]); setRegimeDivFilter([]); setRegimeCountryFilter([]); }}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-500 hover:text-red-700"
                  >
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
                <button
                  onClick={exportRegimeExcel}
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Regime Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500 mb-1">Total Goods Value</div>
              <div className="text-xl font-bold text-slate-800">{fmt(regimeTotals.goods)}</div>
            </div>
            {TARIFF_TYPES.map(t => (
              <div key={t} className={`rounded-xl border border-slate-200 p-4 ${REGIME_BG[t]}`}>
                <div className="text-xs text-slate-500 mb-1">{t}</div>
                <div className={`text-xl font-bold ${REGIME_TEXT[t]}`}>{fmt(regimeTotals[t])}</div>
                {regimeTotals.goods > 0 && regimeTotals[t] > 0 && (
                  <div className="text-xs text-slate-400 mt-1">
                    {((regimeTotals[t] / regimeTotals.goods) * 100).toFixed(1)}% of goods
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Monthly Pivot Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-slate-700">Duty by Tariff Type & Month</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600 sticky left-0 bg-slate-50">Tariff Type</th>
                    {regimeMonths.map(m => (
                      <th key={m} className="text-right px-3 py-2 font-medium text-slate-600 whitespace-nowrap">{m}</th>
                    ))}
                    <th className="text-right px-3 py-2 font-semibold text-slate-800 bg-slate-100">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {regimePivot.map((row, i) => (
                    <tr key={row.type} className={`border-t border-slate-100 ${REGIME_BG[row.type] || ''}`}>
                      <td className={`px-3 py-2 font-medium sticky left-0 ${REGIME_BG[row.type] || 'bg-white'} ${REGIME_TEXT[row.type] || ''}`}>{row.type}</td>
                      {regimeMonths.map(m => (
                        <td key={m} className="text-right px-3 py-2 tabular-nums">{fmt(row[m] || 0)}</td>
                      ))}
                      <td className="text-right px-3 py-2 font-bold tabular-nums bg-slate-100">{fmt(row.total)}</td>
                    </tr>
                  ))}
                  {/* Grand total row */}
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
                    <td className="px-3 py-2 sticky left-0 bg-slate-100">Grand Total</td>
                    {regimeMonths.map(m => {
                      const mTotal = regimePivot.reduce((s, r) => s + (r[m] || 0), 0);
                      return <td key={m} className="text-right px-3 py-2 tabular-nums">{fmt(mTotal)}</td>;
                    })}
                    <td className="text-right px-3 py-2 font-bold tabular-nums">{fmt(regimePivot.reduce((s, r) => s + r.total, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* By Importer Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-500" />
              <h3 className="text-sm font-semibold text-slate-700">Duty by Importer & Tariff Type</h3>
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Importer</th>
                    {TARIFF_TYPES.map(t => (
                      <th key={t} className={`text-right px-3 py-2 font-medium ${REGIME_TEXT[t]}`}>{t}</th>
                    ))}
                    <th className="text-right px-3 py-2 font-semibold text-slate-800">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {regimeByImporter.map((row, i) => (
                    <tr
                      key={row.importer}
                      onClick={() => setRegimeImpFilter(prev =>
                        prev.length === 1 && prev[0] === row.importer ? [] : [row.importer]
                      )}
                      className={`border-t border-slate-100 cursor-pointer transition-colors ${
                        regimeImpFilter.includes(row.importer)
                          ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
                          : i % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100'
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-800 truncate max-w-[220px]">{row.importer}</td>
                      {TARIFF_TYPES.map(t => (
                        <td key={t} className={`text-right px-3 py-2 tabular-nums ${row[t] ? REGIME_TEXT[t] : 'text-slate-300'}`}>
                          {fmt(row[t] || 0)}
                        </td>
                      ))}
                      <td className="text-right px-3 py-2 font-bold tabular-nums">{fmt(row.total)}</td>
                    </tr>
                  ))}
                  {regimeByImporter.length > 0 && (
                    <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                      <td className="px-3 py-2">Total</td>
                      {TARIFF_TYPES.map(t => (
                        <td key={t} className={`text-right px-3 py-2 ${REGIME_TEXT[t]}`}>
                          {fmt(regimeByImporter.reduce((s, r) => s + (r[t] || 0), 0))}
                        </td>
                      ))}
                      <td className="text-right px-3 py-2 font-bold">
                        {fmt(regimeByImporter.reduce((s, r) => s + r.total, 0))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Division Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <button
                onClick={() => setShowRegime(!showRegime)}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700"
              >
                {showRegime ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Building2 className="h-4 w-4 text-violet-500" />
                Duty by Division & Tariff Type
              </button>
            </div>
            {showRegime && (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Division</th>
                      {TARIFF_TYPES.map(t => (
                        <th key={t} className={`text-right px-3 py-2 font-medium ${REGIME_TEXT[t]}`}>{t}</th>
                      ))}
                      <th className="text-right px-3 py-2 font-semibold text-slate-800">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regimeByDivision.map((row, i) => (
                      <tr
                        key={row.division}
                        onClick={() => setRegimeDivFilter(prev =>
                          prev.length === 1 && prev[0] === row.division ? [] : [row.division]
                        )}
                        className={`border-t border-slate-100 cursor-pointer transition-colors ${
                          regimeDivFilter.includes(row.division)
                            ? 'bg-violet-50 ring-1 ring-inset ring-violet-200'
                            : i % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100'
                        }`}
                      >
                        <td className="px-3 py-2 font-medium text-slate-800 truncate max-w-[180px]">{row.division}</td>
                        {TARIFF_TYPES.map(t => (
                          <td key={t} className={`text-right px-3 py-2 tabular-nums ${row[t] ? REGIME_TEXT[t] : 'text-slate-300'}`}>
                            {fmt(row[t] || 0)}
                          </td>
                        ))}
                        <td className="text-right px-3 py-2 font-bold tabular-nums">{fmt(row.total)}</td>
                      </tr>
                    ))}
                    {regimeByDivision.length > 0 && (
                      <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                        <td className="px-3 py-2">Total</td>
                        {TARIFF_TYPES.map(t => (
                          <td key={t} className={`text-right px-3 py-2 ${REGIME_TEXT[t]}`}>
                            {fmt(regimeByDivision.reduce((s, r) => s + (r[t] || 0), 0))}
                          </td>
                        ))}
                        <td className="text-right px-3 py-2 font-bold">
                          {fmt(regimeByDivision.reduce((s, r) => s + r.total, 0))}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Regime loading indicator */}
      {regimeLoading && !loading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 text-violet-500 animate-spin" />
          <span className="ml-2 text-sm text-slate-500">Loading tariff regime data...</span>
        </div>
      )}

      {regimeError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700 text-sm">Regime: {regimeError}</span>
        </div>
      )}
    </div>
  );
}
